import React, { useEffect, useMemo, useRef, useState } from "react";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";

import {
	Accordion,
	Alert,
	ButtonStandard,
	Icon,
	Link,
	ModalConfirmation,
	Skeleton,
	Text,
} from "@performyard/py-components";
import { SIDE_NAV_DEFAULT_THEME_ID } from "@performyard/utils-isomorphic";

import ColorHexInput from "./ColorHexInput";

import SideNavThemePicker, {
	clearNavigationConfigPreview,
	patchNavigationConfigThemePreview,
} from "../../../../containers/SideNavThemePicker/SideNavThemePicker";

import {
	useAdminSettingsHomeBranding,
	useCompanySettings,
	useResetAdminSettingsHomeBranding,
	useSelectSideNavTheme,
	useUpdateAdminSettingsHomeBranding,
	useUploadAdminSettingsHomeLogo,
} from "../../../../api/hooks";
import { useDirtyForm } from "../hooks/useDirtyForm";

import { queryClient } from "../../../../api/queryClient";

import { ADMIN_HOME_ACCORDION_CLASS_NAME, ADMIN_HOME_BUTTON_CLASS_NAME } from "./adminHomeStyles";

type PreferredNavigation = "classic" | "new";
type Values = {
	navColor: string;
	iconColor: string;
	preferredNavigation: PreferredNavigation;
	selectedSideNavThemeId: string;
};

const COLOR_HELPER_ID = "admin-home-color-helper";
const LOGO_HELPER_ID = "admin-home-logo-helper";
const LOGO_ERROR_ID = "admin-home-logo-error";
const DEFAULT_NAV_COLOR = "#FFFFFF";
const DEFAULT_ICON_COLOR = "#525861";
const RESET_CONFIRMATION_MESSAGE =
	"Are you sure you want to reset your branding the PerformYard defaults? Note: It may take several minutes for our servers to reflect the logo change.";
const DEFAULT_BRANDING_VALUES: Values = {
	navColor: DEFAULT_NAV_COLOR,
	iconColor: DEFAULT_ICON_COLOR,
	preferredNavigation: "classic",
	selectedSideNavThemeId: "",
};

const normalizeBrandingValues = (values: Values): Values => ({
	navColor: values.navColor || DEFAULT_NAV_COLOR,
	iconColor: values.iconColor || DEFAULT_ICON_COLOR,
	preferredNavigation: values.preferredNavigation,
	selectedSideNavThemeId: values.selectedSideNavThemeId,
});

const brandingValuesFromServer = (values?: Partial<Values> | null, selectedSideNavThemeId = ""): Values => ({
	navColor: values?.navColor || DEFAULT_NAV_COLOR,
	iconColor: values?.iconColor || DEFAULT_ICON_COLOR,
	preferredNavigation: values?.preferredNavigation ?? "classic",
	selectedSideNavThemeId,
});

const isWhiteBrandingColor = (color: string) =>
	["#ffffff", "#fff", "white", "rgb(255, 255, 255)"].includes(color.toLowerCase());

const applyBrandingColors = (values: Values) => {
	// navColor/iconColor are classic-nav-only; the side nav recolors via the navigation-config query.
	if (values.preferredNavigation === "new") return;

	const normalized = normalizeBrandingValues(values);
	const root = document.documentElement;
	root.style.setProperty("--company-navbar-bg", normalized.navColor);
	root.style.setProperty("--company-icon-color", normalized.iconColor);
	root.style.setProperty("--company-icon-hover", `${normalized.iconColor}dd`);
	root.style.setProperty("--company-icon-active", normalized.iconColor);

	const header = document.getElementById("header");
	if (header instanceof HTMLElement) {
		header.style.backgroundColor = normalized.navColor;
		header.style.borderBottom = isWhiteBrandingColor(normalized.navColor) ? "1px solid var(--py-neutral-2)" : "";
	}

	const currentLogoContainer = document.getElementById("current-logo-container");
	if (currentLogoContainer instanceof HTMLElement) currentLogoContainer.style.backgroundColor = normalized.navColor;

	const mobileNavButton = document.getElementById("mobile-nav-button");
	if (mobileNavButton instanceof HTMLElement) mobileNavButton.style.color = normalized.iconColor;

	document
		.querySelectorAll<HTMLElement>(
			"#header .content .links .top-item i, #header .content .links .top-item .new-text"
		)
		.forEach((element) => {
			element.style.color = normalized.iconColor;
		});
};

const cacheBustUrl = (url: string, version: number) => {
	if (!version) return url;
	const separator = url.includes("?") ? "&" : "?";
	return `${url}${separator}v=${version}`;
};

// Bridge for the server-rendered Tornado nav-bar logo; remove once ff-temp-react-navbar fully ramps and the nav is React-only.
const updateVisibleLogo = (baseUrl: string, version: number) => {
	for (const id of ["logo", "mobile-logo"]) {
		const logo = document.getElementById(id);
		if (logo instanceof HTMLImageElement) {
			const fallbackUrl =
				id === "mobile-logo" ? baseUrl.replace("/company-logo/", "/company-logo-mobile/") : baseUrl;
			const currentUrl = logo.getAttribute("src") || fallbackUrl;
			logo.src = cacheBustUrl(currentUrl.split("?")[0], version);
		}
	}
};

type Props = {
	isOpen: boolean;
	onToggle: () => void;
	canEdit?: boolean;
	companyId?: string;
};

const CustomBrandingSection: React.FC<Props> = ({ isOpen, onToggle, canEdit = true, companyId }) => {
	const { data, isLoading } = useAdminSettingsHomeBranding(companyId);
	const { data: companySettings } = useCompanySettings();
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const [hasUnsupportedFile, setHasUnsupportedFile] = useState(false);
	const [logoVersion, setLogoVersion] = useState(0);
	const [stagedLogoFile, setStagedLogoFile] = useState<File | null>(null);
	const [stagedLogoPreviewUrl, setStagedLogoPreviewUrl] = useState<string | null>(null);
	const [isResetModalOpen, setIsResetModalOpen] = useState(false);
	const savedValuesRef = useRef<Values>(DEFAULT_BRANDING_VALUES);
	const hasCapturedSavedValuesRef = useRef(false);

	const initial: Values = useMemo(() => brandingValuesFromServer(data), [data]);

	const {
		values,
		setField,
		reset,
		discard,
		syncFromServer,
		isDirty: areValuesDirty,
		diff,
	} = useDirtyForm<Values>(initial);
	const isDirty = areValuesDirty || stagedLogoFile !== null;

	useEffect(() => {
		if (!stagedLogoFile) {
			setStagedLogoPreviewUrl(null);
			return;
		}
		const url = URL.createObjectURL(stagedLogoFile);
		setStagedLogoPreviewUrl(url);
		return () => URL.revokeObjectURL(url);
	}, [stagedLogoFile]);

	useEffect(() => {
		if (!data) return;
		const next = brandingValuesFromServer(data, savedValuesRef.current.selectedSideNavThemeId);
		savedValuesRef.current = next;
		hasCapturedSavedValuesRef.current = true;
		syncFromServer(next);
		applyBrandingColors(next);
	}, [data, syncFromServer]);

	useEffect(() => {
		if (hasCapturedSavedValuesRef.current) applyBrandingColors(values);
	}, [values]);

	useEffect(
		() => () => {
			if (hasCapturedSavedValuesRef.current) applyBrandingColors(savedValuesRef.current);
		},
		[]
	);

	const updateMutation = useUpdateAdminSettingsHomeBranding(() => {
		savedValuesRef.current = values;
		hasCapturedSavedValuesRef.current = true;
		reset(values);
		applyBrandingColors(values);
	}, companyId);
	const selectThemeMutation = useSelectSideNavTheme(companyId);
	const uploadMutation = useUploadAdminSettingsHomeLogo(() => {
		setLogoVersion(Date.now());
		setStagedLogoFile(null);
	}, companyId);
	const resetMutation = useResetAdminSettingsHomeBranding(() => {
		const resetValues: Values = {
			...DEFAULT_BRANDING_VALUES,
			preferredNavigation: savedValuesRef.current.preferredNavigation,
			selectedSideNavThemeId: SIDE_NAV_DEFAULT_THEME_ID,
		};
		setIsResetModalOpen(false);
		savedValuesRef.current = resetValues;
		hasCapturedSavedValuesRef.current = true;
		reset(resetValues);
		applyBrandingColors(resetValues);
		patchNavigationConfigThemePreview(queryClient, undefined);
		setStagedLogoFile(null);
		setHasUnsupportedFile(false);
		setLogoVersion(Date.now());
	}, companyId);
	const colorInputsDisabled = !canEdit || updateMutation.isPending || values.preferredNavigation !== "classic";

	const logoBaseUrl = `/company-logo/${companyId ?? companySettings?.company?.id ?? ""}`;
	const logoUrl = cacheBustUrl(logoBaseUrl, logoVersion);
	const displayLogoUrl = stagedLogoPreviewUrl ?? logoUrl;

	useEffect(() => {
		if (logoVersion) updateVisibleLogo(logoBaseUrl, logoVersion);
	}, [logoBaseUrl, logoVersion]);

	const onSave = () => {
		const payload: { navColor?: string; iconColor?: string } = {};
		if (diff.navColor !== undefined && values.preferredNavigation === "classic") payload.navColor = values.navColor;
		if (diff.iconColor !== undefined && values.preferredNavigation === "classic")
			payload.iconColor = values.iconColor;
		const hasPayload = Object.keys(payload).length > 0;
		const shouldSelectTheme =
			values.preferredNavigation === "new" &&
			diff.selectedSideNavThemeId !== undefined &&
			values.selectedSideNavThemeId;
		const commitSavedValues = () => {
			savedValuesRef.current = values;
			hasCapturedSavedValuesRef.current = true;
			reset(values);
			applyBrandingColors(values);
		};
		const saveBranding = () => {
			if (!hasPayload) {
				commitSavedValues();
				return;
			}
			updateMutation.mutate(payload as Parameters<typeof updateMutation.mutate>[0]);
		};

		const saveThemeThenBranding = () => {
			if (shouldSelectTheme) {
				selectThemeMutation.mutate(values.selectedSideNavThemeId, { onSuccess: saveBranding });
			} else {
				saveBranding();
			}
		};

		if (stagedLogoFile) {
			uploadMutation.mutate(stagedLogoFile, { onSuccess: saveThemeThenBranding });
		} else {
			saveThemeThenBranding();
		}
	};

	const onDiscard = () => {
		discard();
		applyBrandingColors(savedValuesRef.current);
		clearNavigationConfigPreview(queryClient);
		setStagedLogoFile(null);
		setHasUnsupportedFile(false);
	};

	const onResetBranding = () => {
		resetMutation.mutate();
	};

	const onLogoSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		event.target.value = "";
		if (!file) return;
		if (file.type !== "image/png" && file.type !== "image/jpeg") {
			setHasUnsupportedFile(true);
			return;
		}
		setHasUnsupportedFile(false);
		setStagedLogoFile(file);
	};

	if (data && !data.tenantFlags.customBrandingEnabled && !data.tenantFlags.canBypassCustomBrandingFlag) {
		return null;
	}

	return (
		<Accordion
			isOpen={isOpen}
			onToggle={onToggle}
			variant="standard"
			className={ADMIN_HOME_ACCORDION_CLASS_NAME}
			headerContent={
				<div className="flex flex-col">
					<Text
						size="paragraph-1"
						dangerouslySetClassName="text-[18px] font-semibold leading-6 text-neutral-9"
					>
						Custom Branding
					</Text>
					<Text size="paragraph-3" dangerouslySetClassName="leading-4 text-neutral-9">
						Update the branding of PerformYard
					</Text>
				</div>
			}
		>
			{isLoading || !data ? (
				<Skeleton className="h-64" />
			) : (
				<div className="-mx-4 -my-3 flex flex-col gap-8 p-8">
					{values.preferredNavigation === "new" ? (
						<section className="flex flex-col gap-4">
							<Text size="paragraph-2" dangerouslySetClassName="font-semibold text-neutral-9">
								Navigation Theme
							</Text>
							<SideNavThemePicker
								companyId={companyId}
								value={values.selectedSideNavThemeId || undefined}
								canEdit={canEdit}
								disabled={selectThemeMutation.isPending}
								onChange={(themeId) => setField("selectedSideNavThemeId", themeId)}
								onPersistedValueChange={(themeId) => {
									if (savedValuesRef.current.selectedSideNavThemeId) return;
									// Picker effect runs before our data effect; stashing on the ref
									// avoids syncing classic defaults into the form before data lands.
									savedValuesRef.current = {
										...savedValuesRef.current,
										selectedSideNavThemeId: themeId,
									};
									if (!hasCapturedSavedValuesRef.current) return;
									syncFromServer(savedValuesRef.current);
									setField("selectedSideNavThemeId", themeId);
								}}
							/>
						</section>
					) : (
						<section className="flex flex-col gap-4">
							<Text size="paragraph-2" dangerouslySetClassName="font-semibold text-neutral-9">
								Navigation Theme
							</Text>
							<div className="flex flex-wrap gap-6">
								<div className="w-[324px]">
									<ColorHexInput
										label="Navigation Bar Color"
										value={values.navColor}
										onChange={(v) => setField("navColor", v)}
										placeholder="#FFFFFF"
										fallbackSwatchColor={DEFAULT_NAV_COLOR}
										ariaDescribedBy={COLOR_HELPER_ID}
										disabled={colorInputsDisabled}
									/>
								</div>
								<div className="w-[324px]">
									<ColorHexInput
										label="Navigation Icon Color"
										value={values.iconColor}
										onChange={(v) => setField("iconColor", v)}
										placeholder="#525861"
										fallbackSwatchColor={DEFAULT_ICON_COLOR}
										ariaDescribedBy={COLOR_HELPER_ID}
										disabled={colorInputsDisabled}
									/>
								</div>
							</div>
							<Text id={COLOR_HELPER_ID} size="paragraph-3" dangerouslySetClassName="text-neutral-9">
								<span className="flex items-center gap-1 text-neutral-9">
									<Icon icon={faCircleInfo} size={16} color="neutral-9" aria-hidden="true" />
									<span>
										Hex colors or RGB color values are supported. For more information{" "}
										<Link
											href="https://www.w3schools.com/colors/colors_picker.asp"
											target="_blank"
											rel="noreferrer"
											underline="hover"
										>
											click here
										</Link>
									</span>
								</span>
							</Text>
						</section>
					)}

					<section className="flex flex-col gap-4">
						<Text size="paragraph-2" dangerouslySetClassName="font-semibold text-neutral-9">
							Logo
						</Text>
						<div className="flex h-[52.5px] max-h-[52.5px] max-w-[375px] items-center overflow-hidden border border-solid border-neutral-2">
							<img className="h-12 max-w-full object-contain" src={displayLogoUrl} alt="Company logo" />
						</div>
						<Text id={LOGO_HELPER_ID} size="paragraph-3" dangerouslySetClassName="leading-4 text-neutral-9">
							<span className="flex items-center gap-1">
								<Icon icon={faCircleInfo} size={16} color="neutral-9" aria-hidden="true" />
								<span>
									All images are scaled to 35px height and cropped to fit within a 35x250px container.
									Only JPG or PNG file types (up to 5MB) accepted.
								</span>
							</span>
						</Text>
						{hasUnsupportedFile ? (
							<div id={LOGO_ERROR_ID} role="alert">
								<Alert type="error" message="Only JPG or PNG file types are accepted." />
							</div>
						) : null}
						<input
							ref={fileInputRef}
							type="file"
							accept="image/png,image/jpeg"
							className="hidden"
							onChange={onLogoSelected}
							aria-label="Upload company logo"
							aria-describedby={hasUnsupportedFile ? LOGO_ERROR_ID : LOGO_HELPER_ID}
						/>
						<div>
							<ButtonStandard
								onClick={() => fileInputRef.current?.click()}
								disabled={!canEdit || uploadMutation.isPending}
								dangerouslySetClassName={ADMIN_HOME_BUTTON_CLASS_NAME}
							>
								Change Logo
							</ButtonStandard>
						</div>
					</section>

					<div className="flex items-center gap-2">
						<ButtonStandard
							onClick={onSave}
							disabled={
								!canEdit ||
								!isDirty ||
								updateMutation.isPending ||
								selectThemeMutation.isPending ||
								uploadMutation.isPending
							}
							dangerouslySetClassName={ADMIN_HOME_BUTTON_CLASS_NAME}
						>
							Save Changes
						</ButtonStandard>
						{isDirty ? (
							<ButtonStandard
								variant="outline"
								color="neutral"
								onClick={onDiscard}
								disabled={
									updateMutation.isPending ||
									selectThemeMutation.isPending ||
									uploadMutation.isPending
								}
								dangerouslySetClassName={ADMIN_HOME_BUTTON_CLASS_NAME}
							>
								Discard
							</ButtonStandard>
						) : null}
						<ButtonStandard
							variant="outline"
							color="neutral"
							onClick={() => setIsResetModalOpen(true)}
							disabled={!canEdit || resetMutation.isPending}
							dangerouslySetClassName={`${ADMIN_HOME_BUTTON_CLASS_NAME} ml-auto`}
						>
							Reset Branding
						</ButtonStandard>
					</div>
					<ModalConfirmation
						isOpen={isResetModalOpen}
						onClose={() => setIsResetModalOpen(false)}
						title="Reset Branding"
						message={RESET_CONFIRMATION_MESSAGE}
						dangerouslySetClassName="w-[520px] max-w-[calc(100vw-32px)]"
						negativeButtonProps={{
							children: "Cancel",
							onClick: () => setIsResetModalOpen(false),
						}}
						positiveButtonProps={{
							children: "Reset Branding",
							onClick: onResetBranding,
							disabled: resetMutation.isPending,
						}}
					/>
				</div>
			)}
		</Accordion>
	);
};

export default CustomBrandingSection;
